import React, { type ComponentType, type LazyExoticComponent } from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { createBrowserRouter } from '@datadog/browser-rum-react/react-router-v6';
import { AtsIntegrationsProvider } from 'context-providers/AtsIntegrationsContext';
import { RouteErrorBoundary } from 'components/RouteErrorBoundary';
import { lazyWithReload } from 'utils/lazyWithReload';
import { ensureEnglishCommon } from 'i18n';
import { paths } from 'url-registry';

import { Layout } from './Layout';
import {
  LoginRoute,
  PreserveSearchRedirect,
  ProtectedRoute,
  RestrictedAccessRoute,
  ShareLinkRoute,
  SharedSubmittalReportRoute,
  SubmittalReportsRoute,
} from './guards';
import { appRouteMountEntries, routeMounts } from './routeMounts';
import type { Mount, PageMount } from './types';

const lazyCache = new Map<
  PageMount['load'],
  LazyExoticComponent<ComponentType<any>> // eslint-disable-line @typescript-eslint/no-explicit-any -- lazy pages have distinct props
>();

function lazyPage(load: PageMount['load']) {
  const cached = lazyCache.get(load);
  if (cached) {
    return cached;
  }
  // Pages wait on English `common` too (see ensureEnglishCommon): the shared
  // components that read it render only inside pages.
  const lazy = lazyWithReload(() =>
    Promise.all([load(), ensureEnglishCommon()]).then(([module]) => module)
  );
  lazyCache.set(load, lazy);
  return lazy;
}

function renderPageMount(mount: PageMount): React.ReactElement {
  const Component = lazyPage(mount.load);
  let element: React.ReactElement;
  switch (mount.guard) {
    case 'protected':
      element = <ProtectedRoute component={Component} />;
      break;
    case 'login':
      element = <LoginRoute component={Component} />;
      break;
    case 'share':
      element = <ShareLinkRoute component={Component} />;
      break;
    case 'submittal':
      element = <SubmittalReportsRoute component={Component} />;
      break;
    case 'submittalShare':
      element = <SharedSubmittalReportRoute component={Component} />;
      break;
    case 'restricted':
      element = <RestrictedAccessRoute component={Component} />;
      break;
    case 'public':
      element = <Component />;
      break;
    default: {
      const _exhaustive: never = mount.guard;
      throw new Error(`Unknown guard: ${_exhaustive}`);
    }
  }
  if (mount.wrapAts) {
    return <AtsIntegrationsProvider>{element}</AtsIntegrationsProvider>;
  }
  return element;
}

function renderMount(mount: Mount): React.ReactElement {
  if (mount.kind === 'redirect') {
    if (mount.preserveSearch) {
      return <PreserveSearchRedirect to={paths[mount.to]} />;
    }
    return <Navigate to={paths[mount.to]} replace />;
  }
  return renderPageMount(mount);
}

export function buildAppRouteChildren(): RouteObject[] {
  const children: RouteObject[] = appRouteMountEntries.map(
    ({ path, mount }) => ({ path, element: renderMount(mount) })
  );
  children.push({
    path: `${paths.SHARE_TRANSCRIPT}/`,
    element: renderMount(routeMounts.SHARE_TRANSCRIPT),
  });
  return children;
}

export function createAppRouter() {
  return createBrowserRouter([
    {
      path: '/',
      element: <Layout />,
      errorElement: <RouteErrorBoundary />,
      children: buildAppRouteChildren(),
    },
  ]);
}
